import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useStackHealth } from '../../hooks/useStackHealth';
import type { HealthStatus, OverallStatus } from '../../types/health';

const STATUS_COLORS: Record<HealthStatus, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

const STATUS_GLOW: Record<HealthStatus, string> = {
  green: 'shadow-emerald-500/50',
  amber: 'shadow-amber-500/50',
  red: 'shadow-red-500/50',
};

const OVERALL_LABELS: Record<OverallStatus, { text: string; color: string }> = {
  nominal: { text: 'Nominal', color: 'text-emerald-400' },
  stressed: { text: 'Stressed', color: 'text-amber-400' },
  alert: { text: 'Alert', color: 'text-red-400' },
};

export const StationVitals: React.FC = () => {
  const { darkMode } = useTheme();
  const { signals, overall, loading, refresh } = useStackHealth();

  if (loading || signals.length === 0) return null;

  const overallInfo = OVERALL_LABELS[overall] || OVERALL_LABELS.nominal;

  return (
    <div
      className={`flex items-center gap-4 px-4 py-2 rounded-lg cursor-pointer transition-all
        ${darkMode ? 'bg-gray-800/40 hover:bg-gray-800/60' : 'bg-gray-50 hover:bg-gray-100'}
      `}
      onClick={() => refresh()}
      title="Click to refresh"
    >
      {/* Overall status word */}
      <span className={`text-xs font-bold uppercase tracking-wider ${overallInfo.color}`}>
        {overallInfo.text}
      </span>

      {/* Divider */}
      <div className={`h-4 w-px ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`} />

      {/* Signal pills */}
      <div className="flex items-center gap-3">
        {signals.map((signal) => (
          <div key={signal.name} className="flex items-center gap-1.5 group relative">
            {/* Status dot */}
            <div
              className={`w-2 h-2 rounded-full ${STATUS_COLORS[signal.status]}
                ${signal.status !== 'green' ? `shadow-md ${STATUS_GLOW[signal.status]}` : ''}
                ${signal.status === 'red' ? 'animate-pulse' : ''}
              `}
            />
            {/* Label */}
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {signal.label}
            </span>

            {/* Tooltip on degraded */}
            {signal.status !== 'green' && (
              <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1
                rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50
                ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-800 text-white'}
              `}>
                {signal.summary}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
